/**
 * Ce qu'un signalement a le droit d'emporter avec lui.
 *
 * Un rapport de bug sans la page d'origine oblige à redemander « vous étiez
 * où ? », et la réponse est rarement exacte. On relève donc l'URL courante à
 * l'envoi — mais telle quelle, elle recopierait des secrets dans une table que
 * tout le staff consulte : `/invitation?token=…` et la réinitialisation de mot
 * de passe portent leur jeton dans la barre d'adresse, et ce jeton suffit à
 * prendre le compte. Ce module est l'endroit unique où l'URL est expurgée,
 * appelé des deux côtés : par le client avant l'envoi (pour que la personne
 * voie ce qui part), et par le serveur avant l'écriture (parce qu'un client
 * n'est pas une garantie).
 */

/**
 * Paramètres retirés de l'URL relevée.
 *
 * La liste est volontairement une liste d'interdiction plutôt qu'une liste
 * d'autorisation : les paramètres légitimes de cette application (onglet
 * courant, filtre, identifiant d'activité) sont précisément ce qui rend un
 * rapport exploitable, et les énumérer un par un les aurait perdus au premier
 * écran ajouté.
 */
const PARAMETRES_SENSIBLES = ['token', 'code', 'password', 'motdepasse', 'email', 'secret', 'otp'];

/** Au-delà, l'URL n'apprend plus rien et remplit la colonne pour rien. */
const LONGUEUR_MAX = 500;

function estSensible(nom: string): boolean {
  const minuscule = nom.toLowerCase();
  return PARAMETRES_SENSIBLES.some((interdit) => minuscule.includes(interdit));
}

/**
 * Réduit une URL à ce qui aide à reproduire un bug : chemin, paramètres
 * anodins, ancre. Le domaine disparaît — il est toujours le même, et le garder
 * n'aurait fait qu'allonger la ligne.
 *
 * Une entrée qui n'est pas une URL analysable ressort tronquée mais conservée :
 * mieux vaut une chaîne approximative qu'un champ vide, et la valeur n'est de
 * toute façon jamais rendue cliquable.
 */
export function sanitizeReportUrl(href: string | null | undefined): string {
  if (!href) return '';
  const brut = String(href).trim();
  if (!brut) return '';

  let url: URL;
  try {
    // La base ne sert qu'aux chemins relatifs ; elle n'apparaît pas en sortie.
    url = new URL(brut, 'https://bala.local');
  } catch {
    return brut.slice(0, LONGUEUR_MAX);
  }

  // `http(s)` seulement : une entrée en `javascript:` ou `data:` n'a rien à
  // faire dans un écran d'administration, même affichée en texte.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return '';
  }

  for (const nom of [...url.searchParams.keys()]) {
    if (estSensible(nom)) url.searchParams.delete(nom);
  }

  // Une ancre peut elle aussi porter un jeton (`#access_token=…`, la forme
  // qu'emploient les redirections OAuth) : on ne garde que les ancres simples.
  const ancre = url.hash.includes('=') ? '' : url.hash;

  return `${url.pathname}${url.search}${ancre}`.slice(0, LONGUEUR_MAX);
}

/**
 * L'ordre compte, et une seule expression régulière alternée ne l'aurait pas
 * respecté : elle rend la correspondance la plus à gauche, or Edge et Opera
 * écrivent tous deux « Chrome » avant leur propre nom. On teste donc du plus
 * spécifique au plus général.
 */
const NAVIGATEURS: Array<[RegExp, string]> = [
  [/\bEdg[A-Z]?\//, 'Edge'],
  [/\bOPR\//, 'Opera'],
  [/\bFirefox\//, 'Firefox'],
  [/\bChrome\//, 'Chrome'],
  [/\bSafari\//, 'Safari'],
];

const SYSTEMES: Array<[RegExp, string]> = [
  [/iPhone/, 'iPhone'],
  [/iPad/, 'iPad'],
  [/Android/, 'Android'],
  [/Macintosh|Mac OS X/, 'Mac'],
  [/Windows/, 'Windows'],
  [/Linux/, 'Linux'],
];

/**
 * Raccourcit un `User-Agent` pour l'affichage. La chaîne complète reste en
 * base : c'est elle qui tranche un doute sur une version de navigateur.
 */
export function shortUserAgent(userAgent: string): string {
  if (!userAgent) return 'Appareil inconnu';

  const navigateur = NAVIGATEURS.find(([motif]) => motif.test(userAgent))?.[1];
  const systeme = SYSTEMES.find(([motif]) => motif.test(userAgent))?.[1];

  if (!navigateur && !systeme) return userAgent.slice(0, 60);
  return [navigateur, systeme].filter(Boolean).join(' · ');
}
